import { CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, FileTextOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, App, Button, Card, Col, Collapse, Modal, Pagination, Progress, Row, Space, Spin, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ContainerFull } from '../components/GeneralComponents'
import { getGpuRunDetails, getGpuSmi, getNextflowLog, pingGpuServers, runGpuAnalysis } from '../endpoints/runEndpoints'

const { Title, Text } = Typography
const { Panel } = Collapse

interface MemoryUsage {
    total: number
    used: number
    free: number
    percentage: number
}

interface GpuInfo {
    id: number
    utilization: number
    memoryUsed: number
    memoryTotal: number
    memoryPercentage: number
    temperature: number
}

interface FolderInfo {
    name: string
    status: 'pending' | 'running' | 'finished'
    size: {
        bytes: number
        mb: string
        gb: string
    }
}

interface GpuServerStatus {
    name: string
    url: string
    status: 'alive' | 'down'
    serverStatus?: string
    timestamp?: string
    cpuUsage?: number
    cpuCores?: number
    memoryUsage?: MemoryUsage
    gpuUsage?: GpuInfo[]
    folders?: FolderInfo[]
    message?: string
}

export const GpuServer = (): JSX.Element => {
    const { modal } = App.useApp()
    const [loading, setLoading] = useState(false)
    const [servers, setServers] = useState<GpuServerStatus[]>([])
    const [error, setError] = useState<string | null>(null)
    const [expandedRunDetails, setExpandedRunDetails] = useState<{[key: string]: any}>({})
    const [loadingDetails, setLoadingDetails] = useState<{[key: string]: boolean}>({})
    const [smiModalVisible, setSmiModalVisible] = useState(false)
    const [smiOutput, setSmiOutput] = useState<string>('')
    const [smiLoading, setSmiLoading] = useState(false)
    const [selectedServer, setSelectedServer] = useState<{name: string, url: string} | null>(null)
    const [runningAnalysis, setRunningAnalysis] = useState<{[key: string]: boolean}>({})
    const [logModalVisible, setLogModalVisible] = useState(false)
    const [logContent, setLogContent] = useState<string>('')
    const [logLoading, setLogLoading] = useState(false)
    const [selectedRun, setSelectedRun] = useState<{serverUrl: string, runId: string, serverName: string} | null>(null)
    const [currentPage, setCurrentPage] = useState<{[serverUrl: string]: number}>({})
    const pageSize = 20

    const fetchRunDetails = useCallback(async (serverUrl: string, runId: string) => {
        const key = `${serverUrl}-${runId}`
        if (expandedRunDetails[key]) {
            return // Already loaded
        }
        
        setLoadingDetails(prev => ({ ...prev, [key]: true }))
        try {
            const result = await getGpuRunDetails({ serverUrl, runId })
            if (result.code === 0 && result.details) {
                setExpandedRunDetails(prev => ({ ...prev, [key]: result.details }))
            }
        } catch (err) {
            console.error('Failed to fetch run details:', err)
        } finally {
            setLoadingDetails(prev => ({ ...prev, [key]: false }))
        }
    }, [expandedRunDetails])

    const checkServerStatus = useCallback(async (fetchFolders: boolean = true) => {
        setLoading(true)
        setError(null)
        try {
            const result = await pingGpuServers()
            if (result.code === 0 && result.servers) {
                setServers(result.servers as GpuServerStatus[])
                // Fetch run details for all folders only if requested
                if (fetchFolders) {
                    result.servers.forEach(server => {
                        if (server.status === 'alive' && server.folders) {
                            server.folders.forEach((folder: any) => {
                                fetchRunDetails(server.url, folder.name)
                            })
                        }
                    })
                }
            } else {
                setError(result.message || 'Failed to get server status')
            }
        } catch (err) {
            setError('Failed to connect to backend')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        checkServerStatus(true)
        
        // // Set up automatic refresh every 10 seconds - update CPU/memory/GPU stats only, not folders
        // const intervalId = setInterval(() => {
        //     checkServerStatus(false)
        // }, 10000)
        
        // // Cleanup interval on unmount
        // return () => clearInterval(intervalId)
    }, [checkServerStatus])

    const showSmiOutput = async (server: {name: string, url: string}) => {
        setSelectedServer(server)
        setSmiModalVisible(true)
        setSmiLoading(true)
        setSmiOutput('')
        
        try {
            const result = await getGpuSmi({ serverUrl: server.url })
            if (result.code === 0 && result.output) {
                setSmiOutput(result.output)
            } else {
                setSmiOutput(`Error: ${result.message || 'Failed to fetch nvidia-smi output'}`)
            }
        } catch (err) {
            setSmiOutput(`Error: ${err instanceof Error ? err.message : 'Failed to fetch nvidia-smi output'}`)
        } finally {
            setSmiLoading(false)
        }
    }

    const handleRunAnalysis = async (serverUrl: string, runId: string, status: string) => {
        // Show warning if status is finished
        if (status === 'finished') {
            modal.confirm({
                title: 'Run Analysis on Completed Run?',
                content: 'This run has already finished analysis. Running again will overwrite existing results. Do you want to continue?',
                okText: 'Yes, Run Analysis',
                cancelText: 'Cancel',
                okType: 'danger',
                onOk: async () => {
                    await executeRunAnalysis(serverUrl, runId)
                }
            })
            return
        }
        
        await executeRunAnalysis(serverUrl, runId)
    }

    const executeRunAnalysis = async (serverUrl: string, runId: string) => {
        const key = `${serverUrl}-${runId}`
        setRunningAnalysis(prev => ({ ...prev, [key]: true }))
        
        try {
            const result = await runGpuAnalysis({ serverUrl, runId })
            if (result.code === 0) {
                message.success(result.message || 'Analysis started successfully')
                // Refresh all server data including folders immediately
                await checkServerStatus(true)
                // Clear loading state after refresh completes
                setRunningAnalysis(prev => ({ ...prev, [key]: false }))
            } else {
                message.error(result.message || 'Failed to start analysis')
                setRunningAnalysis(prev => ({ ...prev, [key]: false }))
            }
        } catch (err) {
            message.error(err instanceof Error ? err.message : 'Failed to start analysis')
            setRunningAnalysis(prev => ({ ...prev, [key]: false }))
        }
    }

    const showNextflowLog = async (serverUrl: string, runId: string, serverName: string) => {
        setSelectedRun({ serverUrl, runId, serverName })
        setLogModalVisible(true)
        setLogLoading(true)
        setLogContent('')
        
        try {
            const result = await getNextflowLog({ serverUrl, runId })
            console.log('Nextflow log result:', result)
            if (result.code === 0 && result.log) {
                setLogContent(result.log)
            } else {
                setLogContent(`Error: ${result.message || 'Failed to fetch nextflow log'}`)
            }
        } catch (err) {
            setLogContent(`Error: ${err instanceof Error ? err.message : 'Failed to fetch nextflow log'}`)
        } finally {
            setLogLoading(false)
        }
    }

    return (
        <ContainerFull>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>GPU Servers</Title>
                <Space>
                    <Button 
                        icon={<ReloadOutlined />}
                        onClick={() => checkServerStatus(false)}
                        loading={loading}
                    >
                        Refresh Stats
                    </Button>
                    <Button 
                        type="primary" 
                        icon={<ReloadOutlined />}
                        onClick={() => checkServerStatus(true)}
                        loading={loading}
                    >
                        Refresh Folders
                    </Button>
                </Space>
            </div>

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            <Row gutter={[16, 16]}>
                {servers.map((server, index) => {
                    return (
                        <Col key={index} xs={24}>
                            <Card
                                title={(
                                    <Space>
                                        {server.status === 'alive' ? (
                                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        ) : (
                                            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                        )}
                                        {server.name}
                                    </Space>
                                )}
                                extra={
                                    <Space>
                                        {server.status === 'alive' && (
                                            <Button 
                                                size="small" 
                                                onClick={() => showSmiOutput({ name: server.name, url: server.url })}
                                            >
                                                Show nvidia-smi
                                            </Button>
                                        )}
                                        {loading ? (
                                            <Spin size="small" />
                                        ) : server.status === 'alive' ? (
                                            <Tag color="green">ONLINE</Tag>
                                        ) : (
                                            <Tag color="red">OFFLINE</Tag>
                                        )}
                                    </Space>
                                }
                            >
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <div>
                                        <Text type="secondary">URL: </Text>
                                        <Text code>{server.url}</Text>
                                    </div>

                                {server.status === 'alive' && (
                                    <>
                                        <Row gutter={[16, 16]}>
                                            {server.cpuUsage !== undefined && (
                                                <Col xs={24} md={12} lg={8}>
                                                    <div>
                                                        <div style={{ marginBottom: 8 }}>
                                                            <Text strong>CPU Usage</Text>
                                                            {server.cpuCores && (
                                                                <Text type="secondary" style={{ marginLeft: 8 }}>({server.cpuCores} cores)</Text>
                                                            )}
                                                        </div>
                                                        <Progress 
                                                            percent={server.cpuUsage} 
                                                            status={server.cpuUsage > 80 ? 'exception' : server.cpuUsage > 60 ? 'normal' : 'success'}
                                                            strokeColor={server.cpuUsage > 80 ? '#ff4d4f' : server.cpuUsage > 60 ? '#faad14' : '#52c41a'}
                                                        />
                                                    </div>
                                                </Col>
                                            )}

                                            {server.memoryUsage && (
                                                <Col xs={24} md={12} lg={8}>
                                                    <div>
                                                        <div style={{ marginBottom: 8 }}>
                                                            <Text strong>Memory Usage</Text>
                                                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                                                ({server.memoryUsage.used.toFixed(1)} GB / {server.memoryUsage.total.toFixed(1)} GB)
                                                            </Text>
                                                        </div>
                                                        <Progress 
                                                            percent={server.memoryUsage.percentage} 
                                                            status={server.memoryUsage.percentage > 80 ? 'exception' : server.memoryUsage.percentage > 60 ? 'normal' : 'success'}
                                                            strokeColor={server.memoryUsage.percentage > 80 ? '#ff4d4f' : server.memoryUsage.percentage > 60 ? '#faad14' : '#52c41a'}
                                                        />
                                                    </div>
                                                </Col>
                                            )}

                                            {server.gpuUsage && server.gpuUsage.length > 0 && (
                                                <Col xs={24} md={24} lg={8}>
                                                    <div>
                                                        <Text strong>GPU Usage</Text>
                                                        {server.gpuUsage.map((gpu) => (
                                                            <div key={gpu.id} style={{ marginTop: 12 }}>
                                                                <div style={{ marginBottom: 8 }}>
                                                                    <Text type="secondary">GPU {gpu.id}</Text>
                                                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                                                        {gpu.temperature}°C | {gpu.memoryUsed} MB / {gpu.memoryTotal} MB
                                                                    </Text>
                                                                </div>
                                                                <Progress 
                                                                    percent={gpu.utilization} 
                                                                    status={gpu.utilization > 80 ? 'exception' : gpu.utilization > 60 ? 'normal' : 'success'}
                                                                    strokeColor={gpu.utilization > 80 ? '#ff4d4f' : gpu.utilization > 60 ? '#faad14' : '#52c41a'}
                                                                />
                                                                <Progress 
                                                                    percent={gpu.memoryPercentage} 
                                                                    status={gpu.memoryPercentage > 80 ? 'exception' : gpu.memoryPercentage > 60 ? 'normal' : 'success'}
                                                                    strokeColor={gpu.memoryPercentage > 80 ? '#ff4d4f' : gpu.memoryPercentage > 60 ? '#faad14' : '#52c41a'}
                                                                    format={() => `Mem ${gpu.memoryPercentage}%`}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>

                                        {server.timestamp && (
                                            <div>
                                                <Text type="secondary">Last Response: </Text>
                                                <Text>{new Date(server.timestamp).toLocaleString()}</Text>
                                            </div>
                                        )}

                                        {server.folders && server.folders.length > 0 && (
                                            <div style={{ marginTop: 16 }}>
                                                <Text strong style={{ marginBottom: 8, display: 'block' }}>Runs ({server.folders.length})</Text>
                                                <Row gutter={[8, 8]}>
                                                    {(() => {
                                                        const page = currentPage[server.url] || 1
                                                        const startIndex = (page - 1) * pageSize
                                                        const endIndex = startIndex + pageSize
                                                        const paginatedFolders = server.folders.slice(startIndex, endIndex)
                                                        
                                                        return paginatedFolders.map((folder: FolderInfo) => {
                                                            const key = `${server.url}-${folder.name}`
                                                            const details = expandedRunDetails[key]
                                                            const detailsLoading = loadingDetails[key]
                                                        
                                                        return (
                                                            <Col key={folder.name} xs={24} sm={12} lg={24} xl={12}>
                                                            <Card 
                                                                title={(
                                                                    <Space>
                                                                        <Text strong>
                                                                            {details?.runInfo?.id ? (
                                                                                <Link to={`/admin/run/${details.runInfo.id}`} style={{ fontWeight: 500 }}>
                                                                                    Run {folder.name}
                                                                                </Link>
                                                                            ) : (
                                                                                `Run ${folder.name}`
                                                                            )}
                                                                        </Text>
                                                                        <Tag>{folder.status.toUpperCase()}</Tag>
                                                                        <Text type="secondary">({folder.size.gb} GB)</Text>
                                                                    </Space>
                                                                )}
                                                              
                                                                style={{ marginBottom: 8}}
                                                            >
                                                                {detailsLoading ? (
                                                                    <Spin size="small" />
                                                                ) : !details ? (
                                                                    <Text type="secondary">Loading details...</Text>
                                                                ) : (
                                                                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                                        {!details.hasSettings && (
                                                                            <Alert message={details.message || 'Settings not found'} type="info" style={{ marginBottom: 8 }} />
                                                                        )}
                                                                        <Space direction='horizontal' size="small" style={{ width: '100%' }} >
                                                                            <Button
                                                                                size="small"
                                                                                icon={<FileTextOutlined />}
                                                                                onClick={() => showNextflowLog(server.url, folder.name, server.name)}
                                                                            >
                                                                                View Log
                                                                            </Button>
                                                                            <Button
                                                                                type="primary"
                                                                                size="small"
                                                                                icon={<PlayCircleOutlined />}
                                                                                loading={runningAnalysis[key]}
                                                                                disabled={folder.status === 'running'}
                                                                                onClick={() => handleRunAnalysis(server.url, folder.name, folder.status)}
                                                                            >
                                                                                {folder.status === 'running' ? 'Running...' : 'Run Analysis'}
                                                                            </Button>
                                                                        </Space>
                                                                        {details && details.runInfo && (
                                                                            <div>
                                                                                {/* <Text strong>{details.runInfo.name || `Run ${details.runInfo.id}`}</Text> */}
                                                                                <div>
                                                                                    <Text style={{ marginLeft: 0}}>
                                                                                        Type: {details.runInfo.type}, ID: {details.runInfo.id}
                                                                                        {details.runInfo.plateIds && details.runInfo.plateIds.length > 0 && 
                                                                                            `, Plates: [${details.runInfo.plateIds.join(', ')}]`}
                                                                                        , Barcodes: {details.barcodeCount || 0}
                                                                                        {details.runInfo.flowCellId && (
                                                                                            <>, Flow Cell: {details.runInfo.flowCellId}
                                                                                            {details.runInfo.flowCellDbId && ` (ID: ${details.runInfo.flowCellDbId})`}</>
                                                                                        )}
                                                                                    </Text>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                       
                                                                        
                                                                        {details && details.orders && details.orders.length > 0 && (
                                                                            <div style={{ marginTop: 8 }}>
                                                                                {/* <Text strong style={{ fontSize: 12 }}>Orders:</Text> */}
                                                                                <Space direction="vertical" style={{ width: '100%', marginTop: 4 }} size={4}>
                                                                                    {details.orders.map((order: any) => {
                                                                                        const zipText = order.zipStatus
                                                                                        
                                                                                        return (
                                                                                            <Card key={order.id}  style={{ backgroundColor: '#fafafa' }}>
                                                                                                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                                                                                    <div>
                                                                                                        <Text strong><Link to={`/admin/order/${order.id}`} style={{ fontWeight: 600 }}>Order {order.id}</Link> </Text>
                                                                                                        
                                                                                                        {order.customerName && (
                                                                                                            <Text type="secondary" style={{ marginLeft: 8 }}>- {order.customerName}</Text>
                                                                                                        )} <Tag>{order.samples?.length || 0} samples</Tag>
                                                                                                    </div>
                                                                                                    {folder.status !== 'running' && folder.status !== 'pending' && (
                                                                                                        <div>
                                                                                                            <Text strong style={{ fontSize: 12 }}> Result: </Text>
                                                                                                            <Space size="small" wrap>
                                                                                                                {order.zipStatus === 'available' ? (
                                                                                                                    <a 
                                                                                                                        href={`/api/download/orderresult/${order.id}?r=${Math.random()}`}
                                                                                                                        target="_blank"
                                                                                                                        rel="noopener noreferrer"
                                                                                                                        style={{ textDecoration: 'none' }}
                                                                                                                    >
                                                                                                                        <Tag color="blue" icon={<DownloadOutlined />} style={{ cursor: 'pointer' }}>
                                                                                                                            {zipText}
                                                                                                                        </Tag>
                                                                                                                    </a>
                                                                                                                ) : (
                                                                                                                    <Tag>{zipText}</Tag>
                                                                                                                )}
                                                                                                                {order.zipSize && (
                                                                                                                    <Tag>{order.zipSize.mb} MB</Tag>
                                                                                                                )}
                                                                                                                {order.zipTimestamp && (
                                                                                                                    <Tag>   
                                                                                                                        {new Date(order.zipTimestamp).toLocaleString()}
                                                                                                                    </Tag>
                                                                                                                )}
                                                                                                            </Space>
                                                                                                        </div>
                                                                                                    )}
                                                                                                   
                                                                                                </Space>
                                                                                            </Card>
                                                                                        )
                                                                                    })}
                                                                                </Space>
                                                                            </div>
                                                                        )}
                                                                    </Space>
                                                                )}
                                                            </Card>
                                                            </Col>
                                                        )
                                                        })
                                                    })()}
                                                </Row>
                                                {server.folders.length > pageSize && (
                                                    <div style={{ marginTop: 16, textAlign: 'center' }}>
                                                        <Pagination
                                                            current={currentPage[server.url] || 1}
                                                            pageSize={pageSize}
                                                            total={server.folders.length}
                                                            onChange={(page) => {
                                                                setCurrentPage(prev => ({ ...prev, [server.url]: page }))
                                                            }}
                                                            showSizeChanger={false}
                                                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} runs`}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}

                                {server.message && (
                                    <Alert
                                        message={server.message}
                                        type="warning"
                                        showIcon
                                        style={{ marginTop: 8 }}
                                    />
                                )}
                            </Space>
                        </Card>
                    </Col>
                    )
                })}

                {servers.length === 0 && !loading && (
                    <Col span={24}>
                        <Alert
                            message="No GPU servers configured"
                            description="Configure GPU servers using the GPU_SERVERS environment variable"
                            type="info"
                            showIcon
                        />
                    </Col>
                )}
            </Row>

            <Modal
                title={`nvidia-smi Output - ${selectedServer?.name || ''}`}
                open={smiModalVisible}
                onCancel={() => setSmiModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setSmiModalVisible(false)}>
                        Close
                    </Button>,
                    <Button 
                        key="refresh" 
                        type="primary"
                        loading={smiLoading}
                        onClick={() => selectedServer && showSmiOutput(selectedServer)}
                    >
                        Refresh
                    </Button>
                ]}
                width={1000}
            >
                {smiLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : (
                    <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '12px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '500px'
                    }}>
                        {smiOutput}
                    </pre>
                )}
            </Modal>

            <Modal
                title={`Nextflow Log - ${selectedRun?.serverName || ''} - Run ${selectedRun?.runId || ''}`}
                open={logModalVisible}
                onCancel={() => setLogModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setLogModalVisible(false)}>
                        Close
                    </Button>,
                    <Button 
                        key="refresh" 
                        type="primary"
                        loading={logLoading}
                        onClick={() => selectedRun && showNextflowLog(selectedRun.serverUrl, selectedRun.runId, selectedRun.serverName)}
                    >
                        Refresh
                    </Button>
                ]}
                width={1200}
            >
                {logLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin />
                    </div>
                ) : (
                    <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '12px', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '70vh',
                        whiteSpace: 'pre',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                    }}>
                        {logContent}
                    </pre>
                )}
            </Modal>
        </ContainerFull>
    )
}

export default GpuServer
